import React from 'react';
import { Button } from '@benzinga/core-ui';
import styles from './QuoteNewsFeed.module.scss';
import type { News, StoryObject } from '@benzinga/basic-news-manager';
import type { SimpleNewsQueryAndOptions } from '@benzinga/internal-news-manager';
import { ContentFeed, NewsListItemElement } from '@benzinga/news';
import { useTranslation } from 'react-i18next';
import { type LocaleType } from '@benzinga/translate';
import { formatTickerToURLFriendly } from '@benzinga/utils';

export interface QuoteNewsFeedProps {
  nodes: (StoryObject | News)[];
  adUnit?: React.ReactNode;
  query?: SimpleNewsQueryAndOptions;
  seeMoreEnabled?: boolean;
  seeMoreLabel?: string;
  dynamicReachContent?: boolean;
  showLoadMoreButton?: boolean;
}

export const QuoteNewsFeed: React.FC<QuoteNewsFeedProps> = ({
  adUnit,
  nodes,
  query = {},
  seeMoreEnabled = false,
  seeMoreLabel = 'See More',
  showLoadMoreButton = false,
}) => {
  const { t, i18n } = useTranslation('quote');
  const tickerForUrl = Array.isArray(query?.tickers) ? query.tickers[0] : query?.tickers;
  const newsUrl = `/quote/${formatTickerToURLFriendly(tickerForUrl ?? '')}/news`;

  return (
    <div className={`${styles.quoteNewsFeedWrapper} flex flex-col`}>
      <div className="quote-news-list">
        <ContentFeed
          adPlacements={[
            {
              index: 2,
              placement_id: 'quote-news-feed-1',
            },
          ]}
          adUnit={adUnit}
          isInfinite={false}
          limit={5}
          loadMore={seeMoreEnabled}
          loadMoreElement={(isLoading, handleLoadMore) => {
            if (!seeMoreEnabled) return <></>;
            if (!nodes.length) return <></>;
            return (
              <div className="see-more-button-wrapper flex justify-center items-center relative mt-8 mb-8">
                <Button
                  {...(showLoadMoreButton ? { isLoading, onClick: handleLoadMore } : { as: 'a', href: newsUrl })}
                  className="show-more-button w-full md:w-auto uppercase z-[2] absolute"
                  variant="flat-light-blue-outlined"
                >
                  {seeMoreLabel}
                </Button>
                <div className="hidden md:flex relative items-center justify-center w-full">
                  <hr className="absolute h-px w-full bg-[#ceddf2] border-0 p-0" />
                  <span className="relative bg-white px-2" />
                </div>
              </div>
            );
          }}
          newsItemElement={(node, index) => {
            return (
              <div
                className="quote-news-item bg-[#F2F8FF66] border border-bzblue-400 rounded p-4"
                key={`quote-news-item-${node.id}-${index}`}
              >
                <NewsListItemElement
                  dateType="med"
                  locale={i18n.language as LocaleType}
                  node={node as StoryObject}
                  showAuthor={true}
                  showTeaser={true}
                  showTicker={false}
                  showTime={false}
                />
              </div>
            );
          }}
          nodes={nodes as News[]}
          noResultsElement={<div>{t('Quote.News.no-news-found')}</div>}
          query={query}
          showSponsoredContent={false}
        />
      </div>
    </div>
  );
};

